import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Select,
  FormErrorMessage,
  VStack,
  Box,
  Text,
  Image,
  useToast,
} from "@chakra-ui/react";
import axiosInstance from "../../helpers/axiosInstance";
import { useCurrency } from "../CurrencyExchange/CurrencyContext";

/**
 * Modal for adding an accommodation item to a trip.
 */
export default function AddAccommodationItemModal({
  isOpen,
  onClose,
  tripId,
  hotels,
  setHotels,
  city,
}) {
  const { convertCurrency, currency } = useCurrency();
  const toast = useToast();

  const [services, setServices] = useState([]);
  const [preferences, setPreferences] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [selectedPreference, setSelectedPreference] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [itemData, setItemData] = useState(null);
  const [errors, setErrors] = useState({});

  // Load services & prefs when opened
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    (async () => {
      try {
        const res = await axiosInstance.get(
          "/bucket-list/filter-third-party-services?category=accommodation"
        );
        const raw = res.data.data?.services || [];
        setServices(raw);
        setFilteredServices(raw);
        setPreferences(Array.from(new Set(raw.map((s) => s.preference))));
        setSelectedPreference("");
        setServiceId("");
        setItemData(null);
      } catch (err) {
        console.error("Error fetching accommodations:", err);
      }
    })();
  }, [isOpen]);

  // Re-filter when preference changes
  useEffect(() => {
    if (!selectedPreference) {
      setFilteredServices(services);
    } else {
      setFilteredServices(
        selectedPreference === "all"
          ? services
          : services.filter((s) => s.preference === selectedPreference)
      );
    }
    setServiceId("");
    setItemData(null);
  }, [selectedPreference, services]);

  // Validation
  const validate = () => {
    const e = {};
    if (!selectedPreference) e.preference = "Preference is required";
    if (!serviceId) e.serviceId = "Property is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Handle Save
  const handleSave = async () => {
    if (!validate()) return;
    try {
      const res = await axiosInstance.post(
        `/bucket-list/${tripId}/add-service-item`,
        {
          service_id: serviceId,
          item_data: itemData,
          type: itemData.preference,
          city,
        }
      );
      if (res.data.success) {
        toast({
          title: "Success",
          description: res.data.message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        // console.log("itemData", itemData);
        const newList = hotels.some((h) => h.id === itemData.id)
          ? hotels // already there → keep as-is
          : [...hotels, itemData]; // else append

        setHotels(newList);
      }
    } catch (err) {
      console.error("Error adding accommodation item:", err);
    }
    onClose();
  };

  // Precompute converted per-night price as a Number
  const convertedPerNight =
    itemData?.price?.perNight != null
      ? parseFloat(
          convertCurrency(itemData.price.perNight, itemData.price.currency)
        )
      : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxH="80vh" overflowY="auto">
        <ModalHeader>Add Accommodation from Bucket List</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isInvalid={!!errors.preference}>
              <FormLabel>Type</FormLabel>
              <Select
                placeholder="Select type"
                value={selectedPreference}
                onChange={(e) => setSelectedPreference(e.target.value)}
              >
                <option value="all">All</option>
                {preferences.map((pref) => (
                  <option key={pref} value={pref}>
                    {pref}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.preference}</FormErrorMessage>
            </FormControl>

            <FormControl
              isInvalid={!!errors.serviceId}
              isDisabled={!selectedPreference}
            >
              <FormLabel>Accommodation</FormLabel>
              <Select
                placeholder="Select accommodation"
                value={serviceId}
                onChange={(e) => {
                  const id = e.target.value;
                  setServiceId(id);
                  const sel = services.find((s) => s._id === id);
                  if (sel) setItemData(sel.data);
                }}
              >
                {filteredServices.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.data.title}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.serviceId}</FormErrorMessage>
            </FormControl>

            {itemData && (
              <Box
                borderWidth="1px"
                borderRadius="md"
                p={4}
                display="flex"
                alignItems="center"
              >
                <Box flex="1">
                  <Text fontSize="lg" fontWeight="bold" noOfLines={2} mb={0}>
                    {itemData.title}
                  </Text>
                  {/* <Text fontSize="sm" mt={1} mb={0}>
                    Price: {itemData.price.currency}{" "}
                    {itemData.price.total.toFixed(2)} total
                  </Text> */}
                  {convertedPerNight != null && (
                    <Text fontSize="sm" fontWeight="normal">
                      {currency} {convertedPerNight.toFixed(2)} per night
                    </Text>
                  )}
                </Box>
                {itemData.images?.[0] && (
                  <Image
                    src={itemData.images[0].medium}
                    boxSize="80px"
                    objectFit="cover"
                    borderRadius="md"
                    ml={4}
                    alt={itemData.title}
                  />
                )}
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose} mr={3} variant="ghost">
            Cancel
          </Button>
          <Button colorScheme="customGreen" onClick={handleSave}>
            Add Accommodation
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
